import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GeofenceTriggerType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import {
  OverflowAwareText,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

interface GeofenceTriggerCardProps {
  triggerType: GeofenceTriggerType;
  driverName: string;
  equipmentName: string;
  orderId: string;
  jobId: string;
  lat: string;
  lon: string;
  occurredAt: string;
  onViewClick?: () => void;
  isLoading?: boolean;
}

export default function GeofenceTriggerCard({
  triggerType,
  driverName,
  equipmentName,
  orderId,
  jobId,
  lat,
  lon,
  occurredAt,
  onViewClick,
  isLoading,
}: GeofenceTriggerCardProps) {
  const theme = useTheme();
  const isEnter = triggerType === GeofenceTriggerType.ENTER;
  const typeColor = isEnter
    ? theme.brandV2.colors.treadGreen
    : theme.brandV2.colors.treadOrangeDark;
  const typeLabel = isEnter ? 'ENTER' : 'EXIT';
  const TriggerIcon = isEnter ? LoginOutlined : LogoutOutlined;

  return (
    <Box
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        '&:first-child': { borderTop: `1px solid ${theme.brandV2.colors.treadGray7}` },
        '&:last-child': { borderBottom: 'none' },
      })}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: `${typeColor}14`,
          borderRadius: '50%',
          display: 'flex',
          flexShrink: 0,
          height: 28,
          justifyContent: 'center',
          width: 28,
        }}
      >
        <TriggerIcon sx={{ width: 16, height: 16, color: typeColor }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography sx={{ color: typeColor, fontSize: '12px', fontWeight: 600 }}>
            {typeLabel}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeOutlined sx={{ height: 12, mt: '-2px', width: 12 }} />
              <Typography sx={{ fontSize: '11px' }}>{occurredAt}</Typography>
            </Box>

            {onViewClick && (
              <SmallLoadingButton
                color="info"
                loading={isLoading}
                onClick={onViewClick}
                size="small"
                variant="text"
              >
                {t('actions.view')}
              </SmallLoadingButton>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            mt: 1,
            rowGap: 0.25,
          }}
        >
          <DetailItem icon="person" label={driverName} />
          <DetailItem icon="truck" label={equipmentName} />
          <DetailItem icon="order" label={orderId} />
          <DetailItem icon="job" label={jobId} />
          <DetailItem
            icon="location"
            label={`${lat}, ${lon}`}
            sx={{ gridColumn: 'span 2' }}
          />
        </Box>
      </Box>
    </Box>
  );
}

interface DetailItemProps {
  icon: 'person' | 'truck' | 'order' | 'job' | 'location';
  label: string;
  sx?: SxProps<Theme>;
}

const iconComponents = {
  person: PersonOutlineOutlined,
  truck: LocalShippingOutlined,
  order: Inventory2Outlined,
  job: WorkOutlineOutlined,
  location: PlaceOutlined,
};

function DetailItem({ icon, label, sx }: DetailItemProps) {
  const IconComponent = iconComponents[icon];

  return (
    <Box
      sx={[
        { display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <IconComponent sx={{ flexShrink: 0, height: 14, width: 14 }} />
      <OverflowAwareText sx={{ fontSize: '11px' }}>{label}</OverflowAwareText>
    </Box>
  );
}
